import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  WorkCollaborationPreferenceValues,
  type ProfileDto,
  type WorkCollaborationPreference as WPref,
} from '@paladin-monorepo/common';
import { getFormatLocale } from '@/utils/locale';
import { AuthFormShell } from '@/components/PageHeader';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router';
import { useAuth, type UserProps } from '@/lib/auth';
import { profileApi } from '@/lib/api/profile';
import { onboardingApi } from '@/lib/api/onboarding';
import { withSupplierOnboardingCompleted } from '@/lib/supplierOnboarding';
import { Loading } from '@/components/Loading';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  ONBOARDING_COUNTRY_CODES,
  suggestedTimeZoneForCountry,
} from '@/lib/onboarding/countryDefaultTimeZone';

type StepId = 'profile' | 'location' | 'preferences' | 'review';

type OnboardingFormValues = {
  jobTitle: string;
  description: string;
  country: string;
  city: string;
  timeZone: string;
  workCollaborationPreferences: WPref[];
};

const PROFILE_FIELDS = ['jobTitle', 'description'] as const satisfies readonly (keyof OnboardingFormValues)[];
const LOCATION_FIELDS = ['country', 'city', 'timeZone'] as const satisfies readonly (keyof OnboardingFormValues)[];
const PREFERENCES_FIELDS = ['workCollaborationPreferences'] as const satisfies readonly (keyof OnboardingFormValues)[];

function resolveStepIndex(stepOrder: StepId[], activeStep: StepId): number {
  const i = stepOrder.indexOf(activeStep);
  if (i >= 0) return i;
  return Math.max(0, stepOrder.length - 1);
}

type SupplierOnboardingWizardProps = {
  user: UserProps;
  profile: ProfileDto;
};

function SupplierOnboardingWizard({ user, profile }: SupplierOnboardingWizardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const showLocation = user.supplierOnboarding?.showAccountLocationStep === true;

  const stepOrder = useMemo<StepId[]>(() => {
    const steps: StepId[] = ['profile'];
    if (showLocation) steps.push('location');
    steps.push('preferences', 'review');
    return steps;
  }, [showLocation]);

  const [activeStep, setActiveStep] = useState<StepId>('profile');
  const stepIndex = resolveStepIndex(stepOrder, activeStep);
  const currentStep = stepOrder[stepIndex] ?? 'profile';

  const validationSchema = useMemo(
    () =>
      yup.object({
        jobTitle: yup
          .string()
          .trim()
          .required(t('onboarding.steps.profile.fields.jobTitle.required'))
          .max(255),
        description: yup
          .string()
          .trim()
          .required(t('onboarding.steps.profile.fields.description.required'))
          .min(10, t('onboarding.steps.profile.fields.description.required')),
        ...(showLocation
          ? {
              country: yup
                .string()
                .trim()
                .length(2, t('onboarding.steps.location.countryRequired'))
                .required(t('onboarding.steps.location.countryRequired')),
              city: yup
                .string()
                .trim()
                .required(t('onboarding.steps.location.cityRequired')),
              timeZone: yup
                .string()
                .trim()
                .required(t('onboarding.steps.location.timeZoneRequired')),
            }
          : {
              country: yup.string().optional(),
              city: yup.string().optional(),
              timeZone: yup.string().optional(),
            }),
        workCollaborationPreferences: yup
          .array()
          .of(yup.string().oneOf(WorkCollaborationPreferenceValues).required())
          .min(1, t('onboarding.steps.preferences.minOne'))
          .required(t('onboarding.steps.preferences.minOne')),
      }),
    [showLocation, t],
  );

  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<OnboardingFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      jobTitle: profile.jobTitle ?? '',
      description: profile.description ?? '',
      country: 'CZ',
      city: '',
      timeZone: suggestedTimeZoneForCountry('CZ'),
      workCollaborationPreferences: WorkCollaborationPreferenceValues.filter(
        (p) => profile.workCollaborationPreferences?.includes(p),
      ),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const jobTitle = watch('jobTitle');
  const description = watch('description');
  const country = watch('country');
  const city = watch('city');
  const timeZone = watch('timeZone');
  const workCollaborationPreferences = watch('workCollaborationPreferences');

  const timeZoneOptions = useMemo(() => {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return [
        'UTC',
        'Europe/Prague',
        'Europe/London',
        'Europe/Berlin',
        'Europe/Paris',
        'America/New_York',
      ];
    }
  }, []);

  const region = getFormatLocale(i18n.language);

  const countryLabel = useCallback(
    (code: string) => {
      try {
        return new Intl.DisplayNames([region], { type: 'region' }).of(code) ?? code;
      } catch {
        return code;
      }
    },
    [region],
  );

  const patchProfile = useMutation({
    mutationFn: profileApi.updateProfile,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['profile'] }),
  });

  const patchLocation = useMutation({
    mutationFn: onboardingApi.updateOperationalLocation,
  });

  const completeOnboarding = useMutation({
    mutationFn: onboardingApi.completeSupplierOnboarding,
    onSuccess: async () => {
      queryClient.setQueryData<UserProps | null>(['/me'], (current) =>
        current ? withSupplierOnboardingCompleted(current) : current,
      );
      await queryClient.invalidateQueries({ queryKey: ['/me'] });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success(t('onboarding.complete.success'));
      navigate('/', { replace: true });
    },
  });

  const goNext = async () => {
    if (currentStep === 'profile') {
      const isValid = await trigger([...PROFILE_FIELDS]);
      if (!isValid) return;
      const values = getValues();
      try {
        await patchProfile.mutateAsync({
          jobTitle: values.jobTitle.trim(),
          description: values.description.trim(),
        });
        const next = stepOrder[stepIndex + 1];
        if (next) setActiveStep(next);
      } catch {
        toast.error(t('onboarding.saveError'));
      }
      return;
    }

    if (currentStep === 'location') {
      const isValid = await trigger([...LOCATION_FIELDS]);
      if (!isValid) return;
      const values = getValues();
      try {
        await patchLocation.mutateAsync({
          country: values.country.trim().toUpperCase(),
          city: values.city.trim(),
          timeZone: values.timeZone.trim(),
        });
        const next = stepOrder[stepIndex + 1];
        if (next) setActiveStep(next);
      } catch {
        toast.error(t('onboarding.saveError'));
      }
      return;
    }

    if (currentStep === 'preferences') {
      const isValid = await trigger([...PREFERENCES_FIELDS]);
      if (!isValid) return;
      const values = getValues();
      try {
        await patchProfile.mutateAsync({
          workCollaborationPreferences: values.workCollaborationPreferences,
        });
        const next = stepOrder[stepIndex + 1];
        if (next) setActiveStep(next);
      } catch {
        toast.error(t('onboarding.saveError'));
      }
    }
  };

  const goBack = () => {
    const prev = stepOrder[stepIndex - 1];
    if (prev) setActiveStep(prev);
  };

  const onComplete = async () => {
    const fieldsToValidate: (keyof OnboardingFormValues)[] = [
      ...PROFILE_FIELDS,
      ...PREFERENCES_FIELDS,
      ...(showLocation ? LOCATION_FIELDS : []),
    ];
    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();
    const payload = {
      jobTitle: values.jobTitle.trim(),
      description: values.description.trim(),
      workCollaborationPreferences: values.workCollaborationPreferences,
      ...(showLocation
        ? {
            operationalLocation: {
              country: values.country.trim().toUpperCase(),
              city: values.city.trim(),
              timeZone: values.timeZone.trim(),
            },
          }
        : {}),
    };
    try {
      await completeOnboarding.mutateAsync(payload);
    } catch (e: unknown) {
      const detail =
        axios.isAxiosError(e) && typeof e.response?.data?.detail === 'string'
          ? e.response.data.detail
          : null;
      toast.error(detail ?? t('onboarding.complete.error'));
    }
  };

  const selectedPrefsList = workCollaborationPreferences ?? [];

  return (
    <AuthLayout hideFooter contentWidth="wide">
      <AuthFormShell
        title={t('onboarding.title')}
        description={t('onboarding.subtitle', { step: stepIndex + 1, total: stepOrder.length })}
        className="px-6 py-8 sm:px-10"
      >
        <Card className="border-border-subtle shadow-none">
          <CardHeader>
            <CardTitle>
              {currentStep === 'profile' && t('onboarding.steps.profile.title')}
              {currentStep === 'location' && t('onboarding.steps.location.title')}
              {currentStep === 'preferences' && t('onboarding.steps.preferences.title')}
              {currentStep === 'review' && t('onboarding.steps.review.title')}
            </CardTitle>
            <CardDescription>
              {currentStep === 'profile' && t('onboarding.steps.profile.description')}
              {currentStep === 'location' && t('onboarding.steps.location.description')}
              {currentStep === 'preferences' && t('onboarding.steps.preferences.description')}
              {currentStep === 'review' && t('onboarding.steps.review.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentStep === 'profile' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="jobTitle">{t('onboarding.steps.profile.fields.jobTitle.label')}</Label>
                  <Input
                    id="jobTitle"
                    {...register('jobTitle')}
                    placeholder={t('onboarding.steps.profile.fields.jobTitle.placeholder')}
                    aria-invalid={!!errors.jobTitle}
                  />
                  {errors.jobTitle && (
                    <p className="text-sm text-destructive">{errors.jobTitle.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">{t('onboarding.steps.profile.fields.description.label')}</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder={t('onboarding.steps.profile.fields.description.placeholder')}
                    rows={5}
                    aria-invalid={!!errors.description}
                    className={cn(errors.description && 'border-destructive focus-visible:ring-destructive')}
                  />
                  {errors.description && (
                    <p className="text-sm text-destructive">{errors.description.message}</p>
                  )}
                </div>
              </>
            )}

            {currentStep === 'location' && (
              <>
                <div className="space-y-2">
                  <Label>{t('onboarding.steps.location.country')}</Label>
                  <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={(code) => {
                          field.onChange(code);
                          setValue('timeZone', suggestedTimeZoneForCountry(code), {
                            shouldValidate: true,
                          });
                        }}
                      >
                        <SelectTrigger className="w-full" aria-invalid={!!errors.country}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ONBOARDING_COUNTRY_CODES.map((code) => (
                            <SelectItem key={code} value={code}>
                              {countryLabel(code)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.country && (
                    <p className="text-sm text-destructive">{errors.country.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('onboarding.steps.location.city')}</Label>
                  <Input
                    id="city"
                    {...register('city')}
                    placeholder={t('onboarding.steps.location.cityPlaceholder')}
                    aria-invalid={!!errors.city}
                  />
                  {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t('onboarding.steps.location.timeZone')}</Label>
                  <Controller
                    name="timeZone"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="w-full" aria-invalid={!!errors.timeZone}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {timeZoneOptions.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {tz}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.timeZone && (
                    <p className="text-sm text-destructive">{errors.timeZone.message}</p>
                  )}
                </div>
              </>
            )}

            {currentStep === 'preferences' && (
              <div className="space-y-3">
                {WorkCollaborationPreferenceValues.map((p) => (
                  <label
                    key={p}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-md border border-border p-3',
                      errors.workCollaborationPreferences && 'border-destructive',
                    )}
                  >
                    <Checkbox
                      checked={selectedPrefsList.includes(p)}
                      onCheckedChange={(checked) => {
                        const next = checked
                          ? [...selectedPrefsList, p]
                          : selectedPrefsList.filter((value) => value !== p);
                        setValue('workCollaborationPreferences', next, { shouldValidate: true });
                      }}
                    />
                    <span className="text-sm">{t(`onboarding.preferences.${p}`)}</span>
                  </label>
                ))}
                {errors.workCollaborationPreferences && (
                  <p className="text-sm text-destructive">
                    {errors.workCollaborationPreferences.message}
                  </p>
                )}
              </div>
            )}

            {currentStep === 'review' && (
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium">{t('onboarding.steps.profile.fields.jobTitle.label')}</span>
                  <p className="text-muted-foreground">{jobTitle || '-'}</p>
                </div>
                <div>
                  <span className="font-medium">{t('onboarding.steps.profile.fields.description.label')}</span>
                  <p className="text-muted-foreground whitespace-pre-wrap">{description || '-'}</p>
                </div>
                {showLocation && (
                  <>
                    <div>
                      <span className="font-medium">{t('onboarding.steps.location.country')}</span>
                      <p className="text-muted-foreground">{countryLabel(country)}</p>
                    </div>
                    <div>
                      <span className="font-medium">{t('onboarding.steps.location.city')}</span>
                      <p className="text-muted-foreground">{city || '-'}</p>
                    </div>
                    <div>
                      <span className="font-medium">{t('onboarding.steps.location.timeZone')}</span>
                      <p className="text-muted-foreground">{timeZone}</p>
                    </div>
                  </>
                )}
                <div>
                  <span className="font-medium">{t('onboarding.steps.preferences.title')}</span>
                  <p className="text-muted-foreground">
                    {selectedPrefsList.map((p) => t(`onboarding.preferences.${p}`)).join(', ') ||
                      '-'}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={goBack}
                disabled={stepIndex === 0 || patchProfile.isPending || patchLocation.isPending}
              >
                {t('generic.back')}
              </Button>
              {currentStep !== 'review' ? (
                <Button
                  type="button"
                  onClick={() => void goNext()}
                  disabled={patchProfile.isPending || patchLocation.isPending}
                >
                  {t('generic.continue')}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => void onComplete()}
                  disabled={completeOnboarding.isPending}
                >
                  {t('onboarding.complete.button')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </AuthFormShell>
    </AuthLayout>
  );
}

export function SupplierOnboardingPage() {
  const { user, isLoading: authLoading } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'SupplierOnboardingPage',
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileApi.getProfile(),
    enabled: !!user,
  });

  if (authLoading || !user || profileLoading || !profile) {
    return (
      <AuthLayout hideFooter>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loading />
        </div>
      </AuthLayout>
    );
  }

  return <SupplierOnboardingWizard user={user} profile={profile} />;
}
